// src/components/Prompts/index.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";

import { styles, btnIcon } from "./styles";
import PromptTile from "./PromptTile";
import PromptEditorDrawer from "./PromptEditorDrawer";
import { useAuthedApi } from "./useAuthedApi";
import {
  PromptsProps,
  ServerPrompt,
  PromptItem,
  Vars,
  Kind,
} from "./types";
import { kindLabel, kindStyles } from "./KindBadge";
type Props = PromptsProps & {
  /** 🔥 REQUIRED: the currently selected session ID (no fallbacks). */
  selectedSessionId: string;
  /** Optional extra variables you already have for the selected session. */
  availableVars?: Vars;
  /** Patient ID passed through */
  patientId?: string;
  /** Id of the summarize prompt the session summarizer runs (preferences). */
  summarizePromptId?: string | null;
  /** Select (or clear) the summarizer prompt from the editor drawer. */
  onSelectSummarizePrompt?: (sel: { id: string; note_format?: "soap" | "dap" | "birp" } | null) => void;
};

const MASTER_KINDS = new Set(["agent", "prepare", "summarize"]);

const Prompts: React.FC<Props> = ({
  availableVars,
  onUseInSession,
  selectedSessionId,
  patientId,
  summarizePromptId,
  onSelectSummarizePrompt,
}) => {
  const { t } = useTranslation();
  const { apiGet, apiPost, apiPut, apiDelete } = useAuthedApi();

  const [items, setItems] = useState<PromptItem[]>([]);
  const [search, setSearch] = useState<string>("");
  const [activeTab, setActiveTab] = useState<"master" | "tools">("master");
  const [kindFilter, setKindFilter] = useState<Set<string>>(new Set());

  // editor state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState<string>("");
  const [editTemplate, setEditTemplate] = useState<string>("");
  const [editKind, setEditKind] = useState<Kind | string>("tool");
  const [editDescription, setEditDescription] = useState<string>("");
  // typed extractor fields (kind === 'trend')
  const [editSource, setEditSource] = useState<string>("");
  const [editOutputType, setEditOutputType] = useState<string>("");
  const [editOuraField, setEditOuraField] = useState<string>("");
  const [editTaxonomy, setEditTaxonomy] = useState<string>(""); // one theme per line
  const [editScaleAnchors, setEditScaleAnchors] = useState<string>("");
  const [editAirtimeThreshold, setEditAirtimeThreshold] = useState<string>("");

  // result
  const [resultText, setResultText] = useState<string>("");

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // ONLY real vars you passed in. No defaults, no dummy.
  const realVars: Vars = useMemo(() => {
    const base: Vars = { ...(availableVars || {}) };
    if (
      (availableVars?.transcript || "").trim().length > 0 &&
      !availableVars?.sessionNotes
    ) {
      base.sessionNotes = availableVars!.transcript!;
    }
    return base;
  }, [availableVars]);

  // Load prompts
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const r = await apiGet<{ ok: boolean; items: ServerPrompt[] }>(
          "/prompts"
        );
        const list = (r.items || []).map<PromptItem>((sp) => ({
          id: sp.id,
          name: sp.title,
          template: sp.content,
          kind: sp.kind,
          description: sp.description || "",
          note_format: sp.note_format,
          source: sp.source,
          output_type: sp.output_type,
          oura_field: sp.oura_field,
          taxonomy: sp.taxonomy,
          scale_anchors: sp.scale_anchors,
          airtime_threshold: sp.airtime_threshold,
        }));
        setItems(list);
      } catch (e: any) {
        setError(e?.message || String(e));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(
      (i) =>
        i.name.toLowerCase().includes(q) ||
        (i.description || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const masterPrompts = useMemo(() => filtered.filter((i) => MASTER_KINDS.has(i.kind)), [filtered]);
  const toolPrompts = useMemo(() => filtered.filter((i) => !MASTER_KINDS.has(i.kind)), [filtered]);

  // Kind filter: collect unique kinds for the active tab, then apply filter
  const activeList = activeTab === "master" ? masterPrompts : toolPrompts;
  const availableKinds = useMemo(() => {
    const kinds = new Set(activeList.map((i) => i.kind));
    return Array.from(kinds).sort();
  }, [activeList]);
  const displayList = useMemo(() => {
    if (kindFilter.size === 0) return activeList;
    return activeList.filter((i) => kindFilter.has(i.kind));
  }, [activeList, kindFilter]);

  function openEditor(id: string) {
    const it = items.find((x) => x.id === id);
    if (!it) return;
    setEditingId(id);
    setEditName(it.name);
    setEditTemplate(it.template);
    setEditKind(it.kind);
    setEditDescription(it.description || "");
    setEditSource(it.source || "");
    setEditOutputType(it.output_type || "");
    setEditOuraField(it.oura_field || "");
    setEditTaxonomy((it.taxonomy || []).join("\n"));
    setEditScaleAnchors(it.scale_anchors || "");
    setEditAirtimeThreshold(
      it.airtime_threshold !== undefined ? String(it.airtime_threshold) : ""
    );
    setResultText("");
  }

  function closeEditor() {
    setEditingId(null);
  }

  function closeEditorIf(id: string) {
    if (editingId === id) closeEditor();
  }

  async function createPromptOnServer(): Promise<PromptItem | null> {
    const title = t("prompts.new");
    const kind: Kind = "tool";
    const description = "Short description.";
    const content =
      "Write your prompt here. The session transcript and notes will be provided automatically.";
    try {
      const r = await apiPost<{ ok: boolean; prompt: ServerPrompt }>(
        "/prompts",
        {
          title,
          kind,
          description,
          content,
        }
      );
      const p = r.prompt;
      const created: PromptItem = {
        id: p.id,
        name: p.title,
        template: p.content,
        kind: p.kind,
        description: p.description || "",
      };
      return created;
    } catch (e: any) {
      setError(e?.message || String(e));
      return null;
    }
  }

  async function updatePromptOnServer(
    id: string,
    patch: Partial<
      Pick<
        PromptItem,
        "name" | "template" | "kind" | "description" | "source" | "output_type"
        | "oura_field" | "taxonomy" | "scale_anchors" | "airtime_threshold"
      >
    >
  ) {
    try {
      const payload: any = {};
      if (patch.name !== undefined) payload.title = patch.name;
      if (patch.template !== undefined) payload.content = patch.template;
      if (patch.kind !== undefined) payload.kind = patch.kind;
      if (patch.description !== undefined) payload.description = patch.description;
      if (patch.source !== undefined) payload.source = patch.source;
      if (patch.output_type !== undefined) payload.output_type = patch.output_type;
      if (patch.oura_field !== undefined) payload.oura_field = patch.oura_field;
      if (patch.taxonomy !== undefined) payload.taxonomy = patch.taxonomy;
      if (patch.scale_anchors !== undefined) payload.scale_anchors = patch.scale_anchors;
      if (patch.airtime_threshold !== undefined) payload.airtime_threshold = patch.airtime_threshold;

      const r = await apiPut<{ ok: boolean; prompt: ServerPrompt }>(
        `/prompts/${encodeURIComponent(id)}`,
        payload
      );
      const sp = r.prompt;
      const updated: PromptItem = {
        id: sp.id,
        name: sp.title,
        template: sp.content,
        kind: sp.kind,
        description: sp.description || "",
        source: sp.source,
        output_type: sp.output_type,
        oura_field: sp.oura_field,
        taxonomy: sp.taxonomy,
        scale_anchors: sp.scale_anchors,
        airtime_threshold: sp.airtime_threshold,
      };

      setItems((prev) => prev.map((it) => (it.id === id ? updated : it)));
    } catch (e: any) {
      setError(e?.message || String(e));
    }
  }

  async function addPrompt() {
    setError(null);
    const item = await createPromptOnServer();
    if (!item) return;
    setItems((prev) => [item, ...prev]);
    setEditingId(item.id);
    setEditName(item.name);
    setEditTemplate(item.template);
    setEditKind(item.kind);
    setEditDescription(item.description || "");
    setEditSource("");
    setEditOutputType("");
    setEditOuraField("");
    setEditTaxonomy("");
    setEditScaleAnchors("");
    setEditAirtimeThreshold("");
    setResultText("");
  }

  async function handleRun() {
    try {
      setError(null);
      setResultText("⏳ Running…");

      if (!selectedSessionId) {
        throw new Error("No selected session id");
      }

      // Build request strictly with the real selected session.
      const path = `/prompts/run?session=${encodeURIComponent(
        selectedSessionId
      )}`;
      const body = {
        template: editTemplate,
        system_instruction: "",
        pinned: Array.isArray((realVars as any).pinned)
          ? (realVars as any).pinned
          : [],
      };

      const res = await apiPost<{ ok: boolean; result: string }>(path, body);
      const out = res.result ?? "";
      setResultText(out);

      if (onUseInSession && editingId) {
        onUseInSession({
          id: editingId,
          name: editName,
          filled: out,
          raw: editTemplate,
          kind: editKind,
        });
      }
    } catch (e: any) {
      setError(e?.message || String(e));
    }
  }

  function handleSave() {
    if (!editingId) return;
    const patch: Parameters<typeof updatePromptOnServer>[1] = {
      name: editName,
      template: editTemplate,
      kind: editKind,
      description: editDescription,
    };
    if (editKind === "trend") {
      patch.source = editSource as PromptItem["source"];
      patch.output_type = editOutputType as PromptItem["output_type"];
      patch.oura_field = editOuraField as PromptItem["oura_field"];
      patch.taxonomy = editTaxonomy
        .split("\n")
        .map((x) => x.trim())
        .filter(Boolean);
      patch.scale_anchors = editScaleAnchors;
      if (editAirtimeThreshold.trim() !== "") {
        const thr = Number(editAirtimeThreshold);
        if (!Number.isFinite(thr) || thr <= 0 || thr >= 1) {
          setError("Airtime threshold must be a number between 0 and 1");
          return;
        }
        patch.airtime_threshold = thr;
      }
    }
    updatePromptOnServer(editingId, patch);
  }



  return (
    <div style={styles.root}>
      <div style={styles.toolbar}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={addPrompt}
            style={btnIcon()}
            title={t("prompts.new")}
            aria-label={t("prompts.new")}
            disabled={activeTab === "master"}
          >
            <Plus size={16} />
          </button>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("prompts.searchPlaceholder")}
            style={styles.search}
          />
        </div>
      </div>
      {availableKinds.length > 1 && (
        <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {availableKinds.map((k) => {
            const active = kindFilter.has(k);
            const badge = kindStyles(k);
            return (
              <button
                key={k}
                onClick={() => setKindFilter((prev) => {
                  const next = new Set(prev);
                  if (next.has(k)) next.delete(k); else next.add(k);
                  return next;
                })}
                style={{
                  ...badge,
                  padding: "4px 10px",
                  fontSize: 11,
                  cursor: "pointer",
                  opacity: active ? 1 : 0.45,
                  transition: "opacity 0.15s",
                }}
              >
                {kindLabel(k)}
              </button>
            );
          })}
        </div>
      )}

      {error && <div style={styles.error}>{error}</div>}
      {loading && <div style={styles.loading}>{t("prompts.loading")}</div>}

      {!loading && (
        <>
          <div style={{ display: "flex", gap: 0, marginBottom: 12 }}>
            <button
              onClick={() => { setActiveTab("master"); setKindFilter(new Set()); }}
              style={{
                padding: "6px 16px",
                fontSize: 13,
                fontWeight: 600,
                border: "1px solid var(--color-border)",
                borderRadius: "8px 0 0 8px",
                background: activeTab === "master" ? "var(--color-brand-ghost)" : "transparent",
                color: activeTab === "master" ? "var(--color-title)" : "var(--color-muted)",
                cursor: "pointer",
              }}
            >
              {t("prompts.masterPrompts")}
            </button>
            <button
              onClick={() => { setActiveTab("tools"); setKindFilter(new Set()); }}
              style={{
                padding: "6px 16px",
                fontSize: 13,
                fontWeight: 600,
                border: "1px solid var(--color-border)",
                borderLeft: "none",
                borderRadius: "0 8px 8px 0",
                background: activeTab === "tools" ? "var(--color-brand-ghost)" : "transparent",
                color: activeTab === "tools" ? "var(--color-title)" : "var(--color-muted)",
                cursor: "pointer",
              }}
            >
              {t("prompts.toolPrompts")}
            </button>
          </div>
          <div style={styles.grid}>
            {displayList.map((it) => (
              <PromptTile
                key={it.id}
                item={it}
                items={items}
                setItems={setItems}
                openEditor={openEditor}
                apiPost={apiPost}
                apiDelete={apiDelete}
                setError={setError}
                closeEditorIf={closeEditorIf}
                readonly={activeTab === "master"}
                isSummarizer={!!summarizePromptId && it.id === summarizePromptId}
                summarizerLabel={t("prompts.summarizerBadge")}
              />
            ))}
            {displayList.length === 0 && (
              <div style={styles.empty}>{t("prompts.noMatch")}</div>
            )}
          </div>
        </>
      )}

      <PromptEditorDrawer
        visible={!!editingId}
        close={closeEditor}
        editName={editName}
        setEditName={setEditName}
        editKind={editKind}
        setEditKind={setEditKind}
        editDescription={editDescription}
        setEditDescription={setEditDescription}
        editTemplate={editTemplate}
        setEditTemplate={setEditTemplate}
        editSource={editSource}
        setEditSource={setEditSource}
        editOutputType={editOutputType}
        setEditOutputType={setEditOutputType}
        editOuraField={editOuraField}
        setEditOuraField={setEditOuraField}
        editTaxonomy={editTaxonomy}
        setEditTaxonomy={setEditTaxonomy}
        editScaleAnchors={editScaleAnchors}
        setEditScaleAnchors={setEditScaleAnchors}
        editAirtimeThreshold={editAirtimeThreshold}
        setEditAirtimeThreshold={setEditAirtimeThreshold}
        resultText={resultText}
        onRun={handleRun}
        onSave={handleSave}
        isSummarizer={!!editingId && editingId === summarizePromptId}
        onSetSummarizer={
          onSelectSummarizePrompt && editingId
            ? (use: boolean) => {
                if (!use) {
                  onSelectSummarizePrompt(null);
                  return;
                }
                const it = items.find((x) => x.id === editingId);
                if (!it) return;
                onSelectSummarizePrompt({ id: it.id, note_format: it.note_format });
              }
            : undefined
        }
      />
    </div>
  );
};

export default Prompts;
